import { useEffect, useRef, useState } from 'react'
import type { ReactElement } from 'react'
import { useSearchParams } from 'react-router'
import { AppRoot } from '../components/AppRoot'
import { Button, TextField } from '../ui'
import { normalizeJoinCode, parseJoinCode } from '../lib/join-code'
import { redeemInvite, REDEEM_MESSAGES } from '../lib/profile'
import { useSession } from '../lib/session-context'
import { SignInScreen } from './SignInScreen'

export interface JoinScreenProps {
  /** Called once, after a redemption resolves to OK or ALREADY_ACTIVE. */
  onRedeemed?: () => void
}

type Phase = 'entering' | 'redeeming'

/**
 * `redeem_invite()`'s two `raise exception` paths (not authenticated, no profile row) fall
 * outside its 8-value `redeem_result` enum and surface as `{ result: null, error }` — see
 * `../lib/profile`'s `redeemInvite` doc comment. This is the fallback text for that case.
 */
const GENERIC_ERROR_MESSAGE =
  'Something went wrong redeeming your code. Try again, or ask an admin for help if it keeps happening.'

/**
 * `/join`: reads `?code=` from the URL, redeems it, and shows the reason on failure.
 *
 * **Never navigates to get a user signed in.** When there's no session, it renders
 * `<SignInScreen />` directly, inline — the invite code already lives in this route's own URL
 * (`/join?code=…`) and this component never leaves that URL, so there's nothing to "hold" across
 * a redirect. Once the OTP verifies, `SessionProvider`'s subscription updates `session` and this
 * component re-renders past the "not signed in" branch on its own; no `onSignedIn` callback is
 * needed.
 *
 * **Auto-redeems exactly once**, only when the code came from the URL (a real invite link) — a
 * `useRef` guard, not `useState`, because it must survive `session`/`loading`/`code` changing
 * after mount without ever firing twice. A code typed manually (no `?code=` in the URL — the
 * `PENDING`-user-without-a-link case, arriving via `PendingNotice`'s plain `/join` link) always
 * requires an explicit "Redeem" click.
 */
export function JoinScreen({ onRedeemed }: JoinScreenProps): ReactElement | null {
  const [searchParams] = useSearchParams()
  const { session, loading, refresh } = useSession()

  const [code, setCode] = useState(() => parseJoinCode(searchParams) ?? '')
  const [phase, setPhase] = useState<Phase>('entering')
  const [resultMessage, setResultMessage] = useState<string | null>(null)

  const autoRedeemFired = useRef(false)

  async function handleRedeem(codeToRedeem: string) {
    setPhase('redeeming')
    const { result } = await redeemInvite(codeToRedeem)

    if (result === 'OK' || result === 'ALREADY_ACTIVE') {
      await refresh()
      onRedeemed?.()
      return
    }

    setResultMessage(result !== null ? REDEEM_MESSAGES[result] : GENERIC_ERROR_MESSAGE)
    setPhase('entering')
  }

  useEffect(() => {
    if (autoRedeemFired.current) return
    if (session === null || loading) return

    const urlCode = parseJoinCode(searchParams)
    if (urlCode === null) return

    autoRedeemFired.current = true
    void handleRedeem(urlCode)
    // Fires at most once per mount, guarded by autoRedeemFired — deliberately not re-run when
    // session/loading/searchParams change afterward.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- see comment above
  }, [session, loading, searchParams])

  if (loading) return null

  if (session === null) {
    // No outer AppRoot here: SignInScreen already wraps itself in exactly one (per its own doc
    // comment) — a second wrap here would double Station Ledger's chrome (StationLedger's own
    // padding-inline plus sl-app's padding-block, both applying twice).
    return <SignInScreen contextMessage="Sign in to redeem your invite code." />
  }

  const redeeming = phase === 'redeeming'
  // The manually-typed code gets exactly the normalization a URL-borne one already gets through
  // `parseJoinCode` — both now call `normalizeJoinCode`. Without this, a pasted newline, a
  // trailing space, or the hyphens people naturally type into a 10-character code all reach
  // `redeem_invite` intact and come back NOT_FOUND ("check for a typo") to someone who typed it
  // correctly. `redeem_invite` itself only does `upper(trim(...))`, which catches neither the
  // hyphens nor the internal whitespace.
  const normalizedCode = normalizeJoinCode(code)

  return (
    <AppRoot>
      <div className="sl-stack">
        <div>
          <h1 className="sl-heading">Join</h1>
          <p className="sl-hint">Enter your invite code to activate your account.</p>
        </div>
        {resultMessage ? (
          <p className="sl-hint" role="alert">
            {resultMessage}
          </p>
        ) : null}
        <TextField
          label="Invite code"
          value={code}
          onChange={setCode}
          disabled={redeeming}
        />
        {redeeming ? <p className="sl-hint">Redeeming your invite…</p> : null}
        <Button
          variant="primary"
          block
          disabled={normalizedCode === '' || redeeming}
          onClick={() => handleRedeem(normalizedCode)}
        >
          Redeem
        </Button>
      </div>
    </AppRoot>
  )
}
